import { useContext, useState, version, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { PopoverContextProvider } from "../popover";
import { FloatingPanelContext } from "./floating-panel-context";
import { DialogContext } from "../dialog/dialog";

const inertValue = version.startsWith("19") ? true : "true";

/**
 * Use the floating panel component if you need to render any content, which have to be always on a screen.
 * This content will be rendered at the bottom corner of the page.
 */
export const ExperimentalFloatingPanel = ({
  content,
  inertIfDialog = true,
}: {
  content: ReactNode;

  /**
   * If true, the component is inert, while there is an opened dialog
   * @defaultValue true
   */
  inertIfDialog?: boolean;
}) => {
  const dialogContext = useContext(DialogContext);
  const floatingPanelContext = useContext(FloatingPanelContext);
  const [popoverContainer, setPopoverContainer] =
    useState<HTMLDivElement | null>(null);
  const { container } = floatingPanelContext;

  if (!container) {
    return null;
  }

  const isDialogOpen = dialogContext ? dialogContext.stack.length > 0 : false;

  return createPortal(
    <PopoverContextProvider
      value={{
        container: popoverContainer,
      }}
    >
      <div
        {...(isDialogOpen &&
          inertIfDialog && {
            inert: inertValue,
            "aria-hidden": "true",
          })}
      >
        {content}
        <div ref={(element) => setPopoverContainer(element)} />
      </div>
    </PopoverContextProvider>,
    container,
  );
};
